import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sliders, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

const COUNTRIES = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Ethiopia', 'Tanzania', 
  'Uganda', 'Senegal', 'Cameroon', 'Ivory Coast', 'Morocco', 'Egypt',
  'USA', 'UK', 'Canada', 'Germany', 'France', 'Australia', 'Netherlands',
  'Italy', 'Spain', 'Sweden', 'Norway', 'Belgium', 'Switzerland'
];

const INTENTS = [
  { id: 'marriage', label: 'Marriage-Minded', emoji: '💍' },
  { id: 'cultural_exchange', label: 'Cultural Exchange', emoji: '🌍' },
  { id: 'long_distance', label: 'Long-Distance', emoji: '✈️' },
  { id: 'travel_partner', label: 'Travel Partner', emoji: '🧳' },
];

const RELIGIONS = ['Christianity', 'Islam', 'Traditional African', 'Hinduism', 'Buddhism', 'Other'];

export default function DiscoveryFiltersModal({ isOpen, onClose, settings, onSave }) {
  const [filters, setFilters] = useState({
    age_min: 18,
    age_max: 60,
    distance_type: 'worldwide',
    preferred_countries: [],
    preferred_intents: [],
    preferred_religions: [],
  });

  useEffect(() => {
    if (settings) {
      setFilters(prev => ({
        ...prev,
        ...settings
      }));
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    const current = filters[field] || [];
    if (current.includes(item)) {
      handleChange(field, current.filter(i => i !== item));
    } else {
      handleChange(field, [...current, item]);
    }
  };

  const handleReset = () => {
    setFilters({
      age_min: 18,
      age_max: 60,
      distance_type: 'worldwide',
      preferred_countries: [],
      preferred_intents: [],
      preferred_religions: [],
    });
  };

  const handleSave = () => {
    onSave(filters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-dark w-full max-w-lg max-h-[85vh] rounded-t-3xl sm:rounded-3xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-dark border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-2">
              <Sliders className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-xl text-white">Discovery Filters</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="Reset filters"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)] space-y-6">
            {/* Age Range */}
            <div>
              <label className="block text-sm text-gray-300 mb-3 font-body">
                Age Range: {filters.age_min} - {filters.age_max}
              </label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <input
                    type="range"
                    min="18"
                    max="80"
                    value={filters.age_min}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val < filters.age_max) {
                        handleChange('age_min', val);
                      }
                    }}
                    className="w-full accent-gold"
                  />
                </div>
                <span className="text-white font-body w-8 text-center">{filters.age_min}</span>
                <span className="text-gray-500">to</span>
                <span className="text-white font-body w-8 text-center">{filters.age_max}</span>
                <div className="flex-1">
                  <input
                    type="range"
                    min="18"
                    max="80"
                    value={filters.age_max}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val > filters.age_min) {
                        handleChange('age_max', val);
                      }
                    }}
                    className="w-full accent-gold"
                  />
                </div>
              </div>
            </div>

            {/* Distance */}
            <div>
              <label className="block text-sm text-gray-300 mb-3 font-body">Distance</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'country', label: 'Same Country' },
                  { id: 'continent', label: 'Same Continent' },
                  { id: 'worldwide', label: 'Worldwide' },
                  { id: 'local', label: 'Nearby Only' }
                ].map(option => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleChange('distance_type', option.id)}
                    className={`px-4 py-3 rounded-xl text-sm font-body transition-colors ${
                      filters.distance_type === option.id
                        ? 'bg-gold text-black'
                        : 'bg-dark-elevated text-white border border-white/10 hover:border-gold/30'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Preferred Countries */}
            <div>
              <label className="block text-sm text-gray-300 mb-3 font-body">
                Preferred Countries {filters.preferred_countries.length > 0 && `(${filters.preferred_countries.length})`}
              </label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {COUNTRIES.map(country => (
                  <button
                    key={country}
                    type="button"
                    onClick={() => toggleArrayItem('preferred_countries', country)}
                    className={`px-3 py-1.5 rounded-full text-xs font-body transition-colors ${
                      filters.preferred_countries.includes(country)
                        ? 'bg-gold text-black'
                        : 'bg-dark-elevated text-white border border-white/10 hover:border-gold/30'
                    }`}
                  >
                    {country}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Leave empty to see all countries</p>
            </div>

            {/* Relationship Intent */}
            <div>
              <label className="block text-sm text-gray-300 mb-3 font-body">Looking For</label>
              <div className="grid grid-cols-2 gap-2">
                {INTENTS.map(intent => (
                  <button
                    key={intent.id}
                    type="button"
                    onClick={() => toggleArrayItem('preferred_intents', intent.id)}
                    className={`px-3 py-2 rounded-xl text-sm font-body transition-colors flex items-center gap-2 ${
                      filters.preferred_intents.includes(intent.id)
                        ? 'bg-gold/20 border-gold text-gold border'
                        : 'bg-dark-elevated text-white border border-white/10 hover:border-gold/30'
                    }`}
                  >
                    <span>{intent.emoji}</span>
                    <span>{intent.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Religion Preference */}
            <div>
              <label className="block text-sm text-gray-300 mb-3 font-body">Religion Preference</label>
              <div className="flex flex-wrap gap-2">
                {RELIGIONS.map(religion => (
                  <button
                    key={religion}
                    type="button"
                    onClick={() => toggleArrayItem('preferred_religions', religion)}
                    className={`px-3 py-1.5 rounded-full text-xs font-body transition-colors ${
                      filters.preferred_religions.includes(religion)
                        ? 'bg-gold text-black'
                        : 'bg-dark-elevated text-white border border-white/10 hover:border-gold/30'
                    }`}
                  >
                    {religion}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-dark border-t border-white/10 p-4 flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 h-12 rounded-full border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 h-12 btn-gold rounded-full font-body font-semibold"
            >
              Apply Filters
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
